/**
 * Route adapter for the shared log area — the route modal's pair of
 * `sprayLogModel.js` (docs/spec/21i-spraywall-logging.md, "Adapter output" and
 * "Route adapter").
 *
 * `LogArea` and its parts know nothing about ascents, day logs or the
 * grade-opinion scale; this file turns the route data into their props. The
 * strings come through an injected `t`, so the helpers stay pure and testable
 * without an i18n provider.
 *
 * `buildLogModel` (in `logModel.js`) is the one place the dots are derived: both
 * adapters feed it normalized entries, so the route and the spray problem cannot
 * drift apart.
 *
 * Client-only on purpose: only web components use it (the route modal, the
 * Spraywall problem view and the difficulty-opinion gauge), so it does not
 * belong in `@sendly/shared` (docs/guides/shared-code.md).
 */
import { CLIMB_KIND, climbAtMillis, attemptEntryId } from "@sendly/shared";
import { buildLogModel, isCount } from "./logModel";

/** The stored values of the grade opinion, relative to the setter's level. */
export const GRADE_OPINION_VALUES = Object.freeze(["easier", "fair", "harder"]);

/** True for a value the `ascents` rules accept as `gradeVote` (null = no opinion). */
export function isGradeOpinion(value) {
  return GRADE_OPINION_VALUES.includes(value);
}

// Under 15 % either way the route reads as fair; from 50 % the lean is the verdict.
const LEAN_PCT = 15;
const CLEAR_PCT = 50;
// One or two votes are not a trend: the gauge still names the verdict, but neutral.
export const TENTATIVE_BELOW = 3;

const opinionCount = (v) => Math.max(0, Number(v) || 0);

/**
 * The difficulty-opinion gauge's rule (docs/spec/11-map-routes.md,
 * "Difficulty-opinion gauge"). One deviation instead of three shares:
 * (harder − easier) / total, so a "fair" vote pulls towards 0. `null` when
 * nobody voted — the caller then renders nothing.
 */
export function gradeOpinionSummary(gradeOpinion) {
  const src = gradeOpinion && typeof gradeOpinion === "object" ? gradeOpinion : {};
  const counts = Object.fromEntries(GRADE_OPINION_VALUES.map((v) => [v, opinionCount(src[v])]));
  const total = counts.easier + counts.fair + counts.harder;
  if (total === 0) return null;
  const deviation = (counts.harder - counts.easier) / total;
  // `+ 0` folds a -0 from Math.round into 0, so a fair route never prints "−0%".
  const pct = Math.round(deviation * 100) + 0;
  const abs = Math.abs(pct);
  const side = pct > 0 ? "Harder" : "Easier";
  const verdict = abs < LEAN_PCT ? "fair"
    : abs < CLEAR_PCT ? `lean${side}`
      : side.toLowerCase();
  return { counts, total, deviation, pct, verdict, tentative: total < TENTATIVE_BELOW };
}

/** The `gradeOpinion` counts summed over route documents (the setter's gauge). */
export function sumGradeOpinions(routes) {
  const sum = { easier: 0, fair: 0, harder: 0 };
  for (const r of Array.isArray(routes) ? routes : []) {
    for (const v of GRADE_OPINION_VALUES) sum[v] += opinionCount(r?.gradeOpinion?.[v]);
  }
  return sum;
}

/**
 * Props for the log area's optional parts — the "Mi a send?" body — and for
 * `LogOpinion`'s grade-opinion control (the block has none: an opinion belongs
 * to a send). Unlike the spray scale there is no window and no consensus frame
 * — a route's level is the gym's colour, not a Font grade, so the scale is a
 * fixed three steps.
 */
export function routeLogBlockConfig({ t }) {
  return {
    whatIsSendBody: t("route.whatIsSendBody"),
    vote: {
      label: t("route.gradeOpinion"),
      hint: t("route.gradeOpinion.hint"),
      // No "no opinion" option: the pills clear on a second tap.
      options: GRADE_OPINION_VALUES.map((value) => ({ value, label: t(`route.gradeOpinion.${value}`) })),
    },
  };
}

// The daily log calls a project day `attempt`; the dots call it a `try`.
const ITEM_KIND = { [CLIMB_KIND.ATTEMPT]: "try", [CLIMB_KIND.SEND]: "send", [CLIMB_KIND.REPEAT]: "repeat" };

/**
 * The view model of one route's log for one climber.
 *
 * `entries` are the climber's own daily-log entries of the route over every
 * day, each carrying its `date` and its map key (`entryId`, or `cid` as
 * `fetchRouteClimbs` names it). `routeId`, when given, drops entries of other
 * routes and an `attempt` entry stored under a foreign key — the
 * one-per-route-and-day key is what the write paths address, so a dot that does
 * not sit under it could not be deleted.
 *
 * `archived` leaves today out of `days` when it has no dot: an archived route
 * is off the wall, so there is nothing to log today.
 */
export function routeLogModel({ ascent = null, entries = [], today = null, routeId = null, archived = false } = {}) {
  const attemptKey = routeId ? attemptEntryId(routeId) : null;
  const items = [];
  let hasTries = false;
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!e || !e.date) continue;
    if (routeId && e.routeId !== routeId) continue;
    const kind = ITEM_KIND[e.kind];
    if (!kind) continue;
    const entryId = e.entryId ?? e.cid ?? null;
    if (!entryId) continue;
    if (kind === "try") {
      if (attemptKey && entryId !== attemptKey) continue;
      hasTries = true;
    }
    items.push({ date: e.date, kind, entryId, at: climbAtMillis(e), attempts: e.attempts });
  }

  // An ascent decides "sent" on its own: a project never creates one, so a
  // route with tries but no ascent is a project, and nothing at all is "none".
  const state = ascent ? "sent" : hasTries ? "project" : "none";
  return buildLogModel({ state, ascent, items, today, archived });
}

/**
 * A tries count as the `ascents` rules accept it: an integer 1–999, or null
 * when nothing says how many tries it took (a send logged without a project).
 */
export function clampAttempts(value) {
  if (value == null) return null;
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(999, n);
}

/**
 * The tries a SEND stores (docs/spec/11-map-routes.md, "What each tap writes").
 *
 * - the send entry: the tries of the sending day, with the send — today's
 *   attempt entry (`todayAttempts`) plus the send's own N, so the day timeline
 *   reads "13 próbából" after 12 tries that day, not "1 próbából";
 * - the ascent: the whole count — the project days' sum plus that day's N.
 *
 * `projectAttempts` already includes today's tries; `todayAttempts` alone
 * still counts as a project when the loaded model lags behind the live doc.
 *
 * A flash is one try (1 / 1). It is refused — logged as a plain send — when the
 * route already has tries (`projectAttempts` > 0, or an attempt entry today):
 * the Flash button is hidden then, this is the backstop for a stale tap. A send
 * from a project with `attempts` null counts its own try as 1. Without a
 * project and with `attempts` null both stay null: the count is unknown, and
 * the ascent leaves the field out.
 */
export function sendAttemptCounts({ flash = false, attempts = null, projectAttempts = 0, todayAttempts = 0 } = {}) {
  const today = Math.max(0, Math.round(Number(todayAttempts)) || 0);
  const prior = Math.max(today, Math.round(Number(projectAttempts)) || 0);
  const isFlash = !!flash && prior === 0;
  if (isFlash) return { isFlash, entryAttempts: 1, ascentAttempts: 1 };
  const stepper = clampAttempts(attempts);
  if (prior > 0) {
    const day = stepper ?? 1;
    return { isFlash, entryAttempts: clampAttempts(today + day), ascentAttempts: clampAttempts(prior + day) };
  }
  return { isFlash, entryAttempts: stepper, ascentAttempts: stepper };
}

/** The GA4 `attempts_bucket` of a send's tries; null when the count is unknown. */
export function attemptsBucket(attempts) {
  if (!isCount(attempts) || attempts < 1) return null;
  if (attempts === 1) return "1";
  if (attempts <= 3) return "2-3";
  if (attempts <= 9) return "4-9";
  return "10+";
}
